import { FiX } from "react-icons/fi";

/**
 * Choice dialog shown when the user clicks "Add Customer Or Location".
 * Lets the user pick between creating a brand new customer or adding a
 * location to an existing customer; the chosen path opens the relevant
 * CustomerAddModal flow.
 */
function AddCustomerOrLocationModal({ onSelectCustomer, onSelectLocation, onCancel }) {
  return (
    <div onClick={onCancel} className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
      <div
        onClick={(e) => e.stopPropagation()}
        data-tour="cust-choice"
        className="relative w-[672px] max-w-full bg-white rounded-[12px] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-[#E5E8F3]">
          <h2 className="text-[20px] leading-[28px] font-OpenSansSemiBold text-[#1A2332] capitalize">
            Customer Or Location
          </h2>
          <FiX size={18} data-tour="cust-choice-close" className="text-[#868DA6] cursor-pointer" onClick={onCancel} />
        </div>

        {/* Body */}
        <div className="px-6 py-6 flex flex-col gap-4">
          <p className="text-[16px] leading-[24px] text-[#5F6477] font-OpenSansRegular">What would you like to add?</p>
          <div className="flex items-stretch gap-3">
            <ChoiceCard
              onClick={onSelectCustomer}
              dataTour="cust-choice-customer"
              title="New Customer"
              description="Create a brand new customer account with its first location."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <g clip-path="url(#clip0_5969_237886)">
                    <path
                      d="M5 18.3327V3.33268C5 2.89065 5.17559 2.46673 5.48816 2.15417C5.80072 1.84161 6.22464 1.66602 6.66667 1.66602H13.3333C13.7754 1.66602 14.1993 1.84161 14.5118 2.15417C14.8244 2.46673 15 2.89065 15 3.33268V18.3327H5Z"
                      stroke="#0065FD"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M4.99996 10H3.33329C2.89127 10 2.46734 10.1756 2.15478 10.4882C1.84222 10.8007 1.66663 11.2246 1.66663 11.6667V16.6667C1.66663 17.1087 1.84222 17.5326 2.15478 17.8452C2.46734 18.1577 2.89127 18.3333 3.33329 18.3333H4.99996"
                      stroke="#0065FD"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M15 7.5H16.6667C17.1087 7.5 17.5326 7.6756 17.8452 7.98816C18.1577 8.30072 18.3333 8.72464 18.3333 9.16667V16.6667C18.3333 17.1087 18.1577 17.5326 17.8452 17.8452C17.5326 18.1577 17.1087 18.3333 16.6667 18.3333H15"
                      stroke="#0065FD"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M8.33337 5H11.6667"
                      stroke="#0065FD"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M8.33337 8.33398H11.6667"
                      stroke="#0065FD"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M8.33337 11.666H11.6667"
                      stroke="#0065FD"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M8.33337 15H11.6667"
                      stroke="#0065FD"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_5969_237886">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              }
            />
            <ChoiceCard
              onClick={onSelectLocation}
              dataTour="cust-choice-location"
              title="New Location"
              description="Add a new location to an existing customer account."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M10.5009 18.1652C12.0509 16.8268 16.6667 12.4935 16.6667 8.33268C16.6667 6.56457 15.9643 4.86888 14.7141 3.61864C13.4638 2.36839 11.7682 1.66602 10 1.66602C8.23193 1.66602 6.53624 2.36839 5.286 3.61864C4.03575 4.86888 3.33337 6.56457 3.33337 8.33268C3.33337 12.4935 7.94921 16.8268 9.49921 18.1652C9.6436 18.2738 9.81938 18.3325 10 18.3325C10.1807 18.3325 10.3565 18.2738 10.5009 18.1652Z"
                    stroke="#0065FD"
                    stroke-width="1.66667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10 10.834C11.3807 10.834 12.5 9.7147 12.5 8.33398C12.5 6.95327 11.3807 5.83398 10 5.83398C8.61929 5.83398 7.5 6.95327 7.5 8.33398C7.5 9.7147 8.61929 10.834 10 10.834Z"
                    stroke="#0065FD"
                    stroke-width="1.66667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              }
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-start justify-end px-6 py-4 border-t border-[#E5E8F3]">
          <button
            type="button"
            onClick={onCancel}
            className="h-[40px] px-1 rounded-[6px] text-[16px] leading-[24px] font-OpenSansSemiBold text-[#7C829A] capitalize"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ChoiceCard({ onClick, title, description, icon, dataTour }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-tour={dataTour}
      className="group flex-1 text-left p-5 rounded-[12px] border border-[#E5E8F3] bg-white hover:border-[#73A7FF] hover:bg-[#F5F9FF] transition-colors flex flex-col gap-3"
    >
      <span className="w-[40px] h-[40px] rounded-[8px] bg-[#EAF3FF] flex items-center justify-center">{icon}</span>
      <span className="flex flex-col gap-1">
        <span className="text-[16px] leading-[24px] font-OpenSansSemiBold text-[#1A2332] capitalize">{title}</span>
        <span className="text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477] font-OpenSansRegular">
          {description}
        </span>
      </span>
    </button>
  );
}

export default AddCustomerOrLocationModal;
